import { useCallback, useEffect, useState } from "react";

import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import {
  Expand as ExpandIcon,
  Screen as FullScreenIcon,
} from "@hl/base-components/lib/assets/icons.generated/index";
import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { Video } from "@hl/base-components/lib/video";
import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import { TransactionStateType } from "@hl/shared-features/lib/features/evm-tx/TransactionContext";
import { AppSEO, NotFound } from "@hl/shared-features/lib/features/layout";
import useResourceType from "@hl/shared-features/lib/hooks/useResourceType";
import { constructDFSGatewayUrl } from "@hl/shared-features/lib/utils/dfs";
import {
  ActionIcon,
  AppShell,
  Box,
  createStyles,
  Flex,
  Footer,
  Group,
  Image,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useDisclosure, useFullscreen } from "@mantine/hooks";
import pRetry from "p-retry";
import { useNavigate, useParams } from "react-router-dom";

import { getDetailPageUrl } from "config";
import { ImageFullscreenModal } from "~components/ImageFullscreenModal";
import { useToolbarStyles } from "~features/MintPage/apollo/GenArtMintedApollo";
import { shloms404OnImageError } from "~features/MintPage/custom/Shloms404";
import { StrabismusSlideMenu } from "~features/MintPage/custom/Strabismus";
import RefreshTokenMetadataButton from "~features/MintPage/refresh-metadata/RefreshTokenMetadataButton";
import { AppHeader } from "~features/layout/Header";
import { GenArtMintedView } from "~features/token-details/GenArtMinted";
import MarketplaceContextWrapper from "~features/token-details/MarketplaceContextWrapper";
import TokenDetailsCardSection from "~features/token-details/TokenDetailsCardSection";
import TokenDetailsCardSection1155 from "~features/token-details/TokenDetailsCardSection1155";
import TokenDetailsSidebarHistory from "~features/token-details/TokenDetailsSidebarHistory";
import TokenDetailsTitleSection from "~features/token-details/TokenDetailsTitleSection";
import TokenDetailsNavigation from "~features/token-details/apollo/TokenDetailsNavigation";
import TokenDetailsSidebar from "~features/token-details/apollo/TokenDetailsSidebar";
import {
  useTokenDetailsQuery,
  useTokensDetailsNormalizedQuery,
} from "~features/token-details/queries/details.graphql.generated";
import { getFullTokenTitle } from "~features/token-details/utils";
import { useMintPage } from "~hooks/useMintPage";
import { MintContext } from "~hooks/useMintState";

import {
  CollectionContractType,
  NftContractStandard,
  SupportedMediaType,
} from "../../../apollo/graphql.generated";
import { ThreeDModelViewer } from "../../MintPage/viewer/3d";
import { AudioArtwork } from "../../MintPage/viewer/AudioArtwork";

const useStyles = createStyles(
  (
    theme,
    {
      hasLoader,
      mediaLoading,
    }: {
      hasLoader?: boolean;
      mediaLoading: boolean;
    }
  ) => {
    return {
      header: {
        backgroundColor: theme.colors.baseBackground[0],
        borderBottom: `1px solid ${theme.colors.divider[0]}`,
      },
      mediaSectionContainer: {
        width: "100%",
        [theme.fn.largerThan("sm")]: {
          flex: "7 !important",
          height: "100%",
        },
      },
      mediaSection: {
        justifyContent: "center",
        gap: 24,
        position: "relative",
        [theme.fn.largerThan("sm")]: {
          height: `calc(100vh - var(--mantine-header-height, 0px))`,
          borderBottom: `1px solid ${theme.colors.divider[0]}`,
          alignItems: "center",
          padding: 40,
          paddingTop: 48,
          gap: 32,
        },
      },
      historySection: {
        justifyContent: "flex-start",
        position: "relative",
        [theme.fn.largerThan("sm")]: {
          alignItems: "stretch",
          padding: 40,
          gap: 0,
        },
      },
      seriesMedia: {
        width: "100%",
        [theme.fn.largerThan("sm")]: {
          height: "100%",
          // Allows image on flexbox to adjust height
          minHeight: 0,
          // For the video's div wrapper
          "> div": {
            height: "100%",
          },
        },
        opacity: hasLoader || mediaLoading ? 0.5 : 1,
        transition: "opacity 200ms ease-in-out",
        pointerEvents: hasLoader || mediaLoading ? "none" : "auto",
      },
      genArtToolbar: {
        marginTop: 24,
        [theme.fn.largerThan("sm")]: {
          marginTop: 32,
        },
      },
      fullHeight: {
        [theme.fn.largerThan("sm")]: {
          height: "100%",
        },
      },
      video: {
        display: "block",
        objectFit: "contain",
        width: "100%",
        height: "100%",
      },
      mobileFooter: {
        padding: 20,
      },
      tokenNavigation: {
        borderBottom: `1px solid ${theme.colors.divider[0]}`,
        padding: "10px 40px",
        alignItems: "center",
        justifyContent: "space-between",
        position: "absolute",
        top: 0,
        height: 69,
        width: "100%",
        opacity: hasLoader ? 0.5 : 1,
        transition: "opacity 200ms ease-in-out",
        pointerEvents: hasLoader ? "none" : "auto",
      },
    };
  }
);

const TokenDetailsApollo = ({
  useTokenByRevealIdQuery,
  modalTokenId,
}: {
  useTokenByRevealIdQuery?: boolean;
  modalTokenId?: string;
}) => {
  const { collectionId, tokenId } = useParams();
  const id = modalTokenId || tokenId;
  const { walletAddress } = useAuth();
  const previousTokenId = (+(id || 0) - 1).toString();
  const nextTokenId = (+(id || 0) + 1).toString();
  const { isMobile } = useMantineMediaQueries({ withInitialValues: true });

  const mintPageHook = useMintPage(collectionId ?? "");
  const {
    collection,
    mintVectorsLoading,
    transactionState,
    collectionLoading,
    isCollectorChoiceMint,
    isCodeGenMint,
    isImported,
    contentTypeInfo,
  } = mintPageHook;

  const {
    data: tokenByRevealIdData,
    loading: tokenByRevealIdLoading,
    refetch: refetchTokenByRevealId,
  } = useTokenDetailsQuery({
    variables: {
      collectionId: collectionId!,
      tokenId: id!,
    },
    skip: !collectionId || !id || !useTokenByRevealIdQuery,
  });

  const userAddress =
    collection?.standard === NftContractStandard.ERC1155
      ? walletAddress ?? null
      : null;

  const {
    data: tokensNormalizedData,
    loading: tokensNormalizedLoading,
    refetch: refetchTokensNormalized,
  } = useTokensDetailsNormalizedQuery({
    variables: {
      collectionId: collectionId!,
      tokenIds: [id!],
      fromS3: true,
      nonFilteredUserAddress: userAddress,
      isImportedCollection:
        collection?.type === CollectionContractType.Imported,
    },
    skip: !collectionId || !id || useTokenByRevealIdQuery,
  });

  const { data: navTokensNormalizedData } = useTokensDetailsNormalizedQuery({
    variables: {
      collectionId: collectionId!,
      tokenIds: [previousTokenId!, nextTokenId!],
      fromS3: true,
      nonFilteredUserAddress: userAddress,
      isImportedCollection:
        collection?.type === CollectionContractType.Imported,
    },
    skip:
      !collectionId ||
      (!previousTokenId && !nextTokenId) ||
      useTokenByRevealIdQuery ||
      !isMobile,
  });

  const currentToken =
    tokensNormalizedData?.getPublicCollection.tokensNormalized?.find(
      (token) => token.tokenId === id
    );

  const [isMinted, setIsMinted] = useState(false);
  const tokenLoading = useTokenByRevealIdQuery
    ? tokenByRevealIdLoading
    : tokensNormalizedLoading;
  const refetch = useTokenByRevealIdQuery
    ? refetchTokenByRevealId
    : refetchTokensNormalized;

  const refetchAfterMint = useCallback(async () => {
    const { data } = await refetchTokensNormalized();
    if (
      !data?.getPublicCollection.tokensNormalized?.find(
        (token) => token.tokenId === id
      )?.minted
    ) {
      throw new Error();
    }
  }, [refetchTokensNormalized]);

  useEffect(() => {
    if (transactionState?.type === TransactionStateType.Done && !isMinted) {
      setIsMinted(true);
      pRetry(refetchAfterMint, {
        retries: 5,
      }).catch();
    }
  }, [transactionState, refetchAfterMint, isMinted]);

  const [isExpanded, { open: expandImage, close: closeImageExpanded }] =
    useDisclosure(false);
  const { ref: resourceRef, toggle: toggleFullscreen } = useFullscreen();

  const token = useTokenByRevealIdQuery
    ? tokenByRevealIdData?.getPublicCollection.seriesToken
    : currentToken;

  const prevToken = useTokenByRevealIdQuery
    ? undefined
    : navTokensNormalizedData?.getPublicCollection.tokensNormalized?.find(
        (token) => token.tokenId === previousTokenId
      );

  const nextToken = useTokenByRevealIdQuery
    ? undefined
    : navTokensNormalizedData?.getPublicCollection.tokensNormalized?.find(
        (token) => token.tokenId === nextTokenId
      );
  const [tokenPersistent, setTokenPersistent] = useState(token);

  const minted = !!tokenPersistent?.minted || isMinted;
  const ownerAddress =
    isMinted && !tokenPersistent?.ownerAddress
      ? walletAddress
      : tokenPersistent?.ownerAddress;
  const tokenUpdated = tokenPersistent
    ? { ...tokenPersistent, minted, ownerAddress }
    : tokenPersistent;
  const showFooter =
    isCollectorChoiceMint || !!tokenPersistent?.minted || isMinted;
  const iconSize = { width: 16, height: 16 };

  const [mediaLoading, setMediaLoading] = useState(false);
  const [loadedUrl, setLoadedUrl] = useState<string>();

  const { classes } = useStyles({
    hasLoader: tokenLoading && !!tokenPersistent,
    mediaLoading,
  });

  const { classes: toolbarClasses } = useToolbarStyles({
    isExpanded,
    isHover: false,
    showToolbarOnHover: false,
    responsive: false,
  });
  const navigate = useNavigate();

  const {
    isVideo: isVideoType,
    isHtml,
    isGltf,
  } = useResourceType(isImported ? token?.animationUrl : null);

  const imageUrl =
    constructDFSGatewayUrl(tokenPersistent?.imageUrl ?? "", true) ||
    collection?.collectionImage ||
    "";

  useEffect(() => {
    if (token) {
      setTokenPersistent(token);
      if (
        loadedUrl !== imageUrl &&
        loadedUrl !== tokenPersistent?.animationUrl
      ) {
        setMediaLoading(true);
      }
    }
  }, [token, loadedUrl, imageUrl, tokenPersistent?.animationUrl]);

  if (!collectionId || !id) {
    return <NotFound />;
  }

  if (
    collectionLoading ||
    (tokenLoading && !tokenPersistent) ||
    (token && !tokenPersistent) ||
    mintVectorsLoading
  ) {
    return <LoadingPage />;
  }

  if (useTokenByRevealIdQuery && collection && token?.tokenId) {
    navigate(getDetailPageUrl(collection, token.tokenId));
  }

  if (!collection || !tokenPersistent || !tokenUpdated) {
    return <NotFound />;
  }

  if (isExpanded) {
    return (
      <ImageFullscreenModal
        imageUrl={tokenPersistent.imageUrl}
        close={closeImageExpanded}
        shloms404UI={collection?.flagVariations.shloms404UI}
      />
    );
  }

  const showIframe = isCodeGenMint || isHtml;

  const isAudio =
    !isImported && contentTypeInfo?.type === SupportedMediaType.AUDIO;

  const isVideo =
    tokenPersistent.animationUrl && !isAudio && (isVideoType || !isImported);

  const imageUrlGenArtView =
    tokenPersistent.metadata?.imageUrlOriginal || tokenPersistent.imageUrl;
  const hasToolbar =
    (tokenPersistent.animationUrl || imageUrl) && !isAudio && !isGltf;

  const content = (
    <Group spacing={isMobile ? "xl" : 0} align="stretch" noWrap={!isMobile}>
      <Box className={classes.mediaSectionContainer}>
        <Stack className={classes.mediaSection}>
          {isMobile && (
            <Box pt={modalTokenId ? 16 : 0}>
              <TokenDetailsTitleSection token={tokenUpdated} />
            </Box>
          )}
          {showIframe && (
            <GenArtMintedView
              animationUrl={tokenPersistent.animationUrl ?? ""}
              imageUrl={imageUrlGenArtView || collection.collectionImage || ""}
              toolbarExtraClass={classes.genArtToolbar}
              navigation={{
                nextTokenId: nextToken?.tokenId,
                prevTokenId: prevToken?.tokenId,
                collection,
              }}
              showNavigation={isMobile}
              tokenId={tokenPersistent.tokenId}
            />
          )}
          {!showIframe && (
            <>
              <TokenDetailsNavigation
                collection={collection}
                nextTokenId={nextToken?.tokenId}
                prevTokenId={prevToken?.tokenId}
                showNavigation={isMobile}
                persistNavigation
              >
                <Box
                  className={classes.seriesMedia}
                  mih={tokenLoading ? "300px" : 0}
                >
                  {isAudio ? (
                    <Box mt={100}>
                      <AudioArtwork
                        animationUrl={tokenPersistent.animationUrl ?? ""}
                        imageUrl={imageUrl}
                        onLoad={() => {
                          setMediaLoading(false);
                          setLoadedUrl(tokenPersistent.animationUrl ?? "");
                        }}
                        type={"details"}
                      />
                    </Box>
                  ) : isVideo ? (
                    <Video
                      className={classes.video}
                      autoPlay
                      loop
                      muted
                      playsInline
                      onLoadedMetadata={() => {
                        setMediaLoading(false);
                        setLoadedUrl(tokenPersistent.animationUrl ?? "");
                      }}
                    >
                      <source
                        src={tokenPersistent.animationUrl ?? ""}
                        ref={resourceRef}
                      />
                    </Video>
                  ) : isGltf ? (
                    <ThreeDModelViewer
                      modelUrl={tokenPersistent.animationUrl ?? ""}
                      onLoad={() => {
                        setMediaLoading(false);
                        setLoadedUrl(tokenPersistent.animationUrl ?? "");
                      }}
                    />
                  ) : (
                    <Image
                      classNames={{
                        image: classes.fullHeight,
                        imageWrapper: classes.fullHeight,
                        figure: classes.fullHeight,
                        root: classes.fullHeight,
                      }}
                      styles={{ image: { height: "100% !important" } }}
                      src={imageUrl}
                      sx={{ cursor: "pointer" }}
                      onClick={expandImage}
                      fit="contain"
                      onError={
                        collection?.flagVariations.shloms404UI
                          ? shloms404OnImageError
                          : undefined
                      }
                      onLoad={() => {
                        setMediaLoading(false);
                        setLoadedUrl(imageUrl);
                      }}
                      imageRef={resourceRef}
                    />
                  )}
                  {collection.flagVariations.strabismusUI && (
                    <Box mt={{ sm: 84 }} h="unset">
                      <StrabismusSlideMenu name={tokenPersistent.name} />
                    </Box>
                  )}
                </Box>
              </TokenDetailsNavigation>
              {hasToolbar ? (
                <Flex justify="center">
                  <Flex p={4} className={toolbarClasses.toolbar} mt={0}>
                    <Group spacing={4}>
                      {!isExpanded && (
                        <Tooltip label="Expand">
                          <ActionIcon onClick={expandImage}>
                            <ExpandIcon {...iconSize} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {contentTypeInfo?.type !== SupportedMediaType.VIDEO && (
                        <Tooltip
                          label="Fullscreen"
                          className={toolbarClasses.fullscreenButton}
                        >
                          <ActionIcon onClick={toggleFullscreen}>
                            <FullScreenIcon {...iconSize} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {token?.tokenId && (
                        <RefreshTokenMetadataButton tokenId={token.tokenId} />
                      )}
                    </Group>
                  </Flex>
                </Flex>
              ) : (
                token?.tokenId && (
                  <RefreshTokenMetadataButton tokenId={token.tokenId} />
                )
              )}
            </>
          )}
        </Stack>
        {!isMobile && (
          <Stack className={classes.historySection} spacing="xs">
            <Text fw={WEIGHT_BOLD} mb="xs">
              History
            </Text>
            <TokenDetailsSidebarHistory token={tokenUpdated} />
          </Stack>
        )}
      </Box>
      <TokenDetailsSidebar
        modalTokenId={modalTokenId}
        token={tokenUpdated}
        tokenLoading={tokenLoading}
      />
    </Group>
  );

  return (
    <MintContext.Provider value={mintPageHook}>
      <MarketplaceContextWrapper
        token={tokenPersistent}
        onTokenRefresh={refetch}
      >
        {modalTokenId ? (
          content
        ) : (
          <>
            <AppSEO
              title={`${getFullTokenTitle(
                collection.name,
                tokenPersistent.name
              )} - Details`}
              description={tokenPersistent.description}
            />
            <AppShell
              padding={!isMobile ? 0 : undefined}
              header={<AppHeader className={classes.header} />}
              footer={
                isMobile && showFooter ? (
                  <Footer className={classes.mobileFooter} height="unset">
                    {collection.standard === NftContractStandard.ERC1155 ? (
                      <TokenDetailsCardSection1155 token={tokenUpdated} />
                    ) : (
                      <TokenDetailsCardSection isFooter token={tokenUpdated} />
                    )}
                  </Footer>
                ) : undefined
              }
            >
              {content}
            </AppShell>
          </>
        )}
      </MarketplaceContextWrapper>
    </MintContext.Provider>
  );
};

export default TokenDetailsApollo;
